import { useCallback, useMemo } from 'react'
import { usePRFileSections } from '@sketch-hq/sketch-web-renderer'

import { useNavigateToFrame } from './useNavigateToFrame'
import { useNavigateToSection } from './useNavigateToSection'
import type { GoToArtboardEventActionType } from '@sketch/canvas'

const EMPTY_ARRAY = [] as const

/**
 * Opens a canvas entity - a frame or a Section - through whichever route suits
 * it, resolved from the page's Sections.
 *
 * Both underlying hooks are Preview-aware, navigating through the matching
 * preview route inside a Preview. The choice between them was written out at
 * three separate call sites: the view-mode overlay props, the view-mode
 * keyboard shortcuts and the inspector's selection overlay. The keyboard one
 * already resolved it this way, from the UUID alone.
 */
export function useNavigateToEntity() {
  const navigateToFrame = useNavigateToFrame()
  const navigateToSection = useNavigateToSection()
  const sections = usePRFileSections() ?? EMPTY_ARRAY

  const sectionObjectIds = useMemo(
    () => new Set(sections.map(section => section.objectId)),
    [sections]
  )

  return useCallback(
    (uuid: string, actionType: GoToArtboardEventActionType) => {
      if (sectionObjectIds.has(uuid)) {
        navigateToSection(uuid, actionType)
      } else {
        navigateToFrame(uuid, actionType)
      }
    },
    [navigateToFrame, navigateToSection, sectionObjectIds]
  )
}
